import { Card } from "@/components/atoms/card";
import { Button } from "@/components/atoms/button";
import { RectangleEllipsis, Mail, CheckCircle } from "lucide-react";
import InvitationStatusMessage from "@/components/modals/InvitationStatusMessage";
import { usePasswordChange } from "@/hooks/usePasswordChange";

interface PasswordSectionProps {
  userData: any;
}

export default function PasswordSection({ userData }: PasswordSectionProps) {
  const passwordChange = usePasswordChange({ userData });

  return (
    <>
      {/* Card 3: Título Contraseña */}
      <Card className="bg-white p-3 sm:p-4 md:p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <RectangleEllipsis className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          <h2 className="text-sm sm:text-base font-semibold">Contraseña</h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-500">
          Solicita un cambio de contraseña. Se enviará un email con las
          instrucciones para crear una nueva contraseña.
        </p>
      </Card>

      {/* Card 4: Botón para solicitar cambio */}
      <Card className="bg-white p-3 sm:p-4 md:p-6 flex justify-center">
        <div className="max-w-md w-full space-y-3 sm:space-y-4">
          {/* Mostrar instrucciones solo si el email NO ha sido enviado */}
          {!passwordChange.emailSentSuccessfully && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-blue-900 mb-1">
                    Cambio de contraseña por email
                  </h3>
                  <p className="text-xs sm:text-sm text-blue-800">
                    Al hacer clic en el botón, recibirás un email en{" "}
                    <strong className="break-all">
                      {userData.auth0Profile?.email}
                    </strong>{" "}
                    con un enlace seguro para crear una nueva contraseña.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mostrar mensaje de éxito si el email ya fue enviado */}
          {passwordChange.emailSentSuccessfully && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-green-900 mb-1">
                    Email enviado exitosamente
                  </h3>
                  <p className="text-xs sm:text-sm text-green-800">
                    Revisa tu bandeja de entrada y{" "}
                    <strong>carpeta de SPAM</strong> en{" "}
                    <strong className="break-all">
                      {userData.auth0Profile?.email}
                    </strong>{" "}
                    y sigue las instrucciones para cambiar tu contraseña. Los
                    emails de cambio de contraseña suelen llegar a la carpeta de
                    correo no deseado.
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={passwordChange.handlePasswordChange}
            className={`w-full text-white disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm py-2 sm:py-2.5 ${
              passwordChange.emailSentSuccessfully
                ? "bg-green-600 hover:bg-green-600"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
            disabled={
              passwordChange.isChangingPassword ||
              passwordChange.emailSentSuccessfully
            }
          >
            {passwordChange.isChangingPassword
              ? "Enviando email..."
              : passwordChange.emailSentSuccessfully
                ? "Email enviado"
                : "Enviar email"}
          </Button>
        </div>
      </Card>

      {/* Modal de cambio de contraseña */}
      {passwordChange.passwordChangeStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <InvitationStatusMessage
              type={passwordChange.passwordChangeStatus}
              errorMessage={passwordChange.passwordChangeError}
              onClose={passwordChange.handleClosePasswordModal}
              mode="password_change"
            />
          </div>
        </div>
      )}
    </>
  );
}
